import React, { forwardRef } from 'react'
import { UseGetProps } from 'restful-react'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import { useAutocompleteRestfulQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteRestfulQuery'
import {
  TAutocompleteBaseChangeHandler,
  IAutocompleteBaseOption,
  TAutocompleteInputChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { useDebouncedCallback } from 'use-debounce/lib'
import {
  useSearchTrailers,
  SearchTrailersResponse,
  SearchTrailersPathParams,
  SearchTrailersQueryParams,
} from 'apps/fleet-planner/types'

type TBaseValue = Pick<IAutocompleteBaseOption, 'id' | 'text'>

/**
 * @note Autocomplete always uses the `id` prop to match up options
 */
const getComputedValue = (
  options: IAutocompleteBaseOption[],
  valueProp: TBaseValue | null
): IAutocompleteBaseOption | null => {
  const { id: idProp } = valueProp || {}
  const option = options.find(({ id }) => id === idProp)
  return option || (valueProp as IAutocompleteBaseOption) || null
}

export interface IAutocompleteTrailerNumberProps<V extends TBaseValue = TBaseValue> {
  value: V | null
  onChange: (newVal: V | null) => void
  shipmentId: string
  shareToken: string
  options?: IAutocompleteBaseOption[]
}

export const useTrailers = (
  props: Omit<
    UseGetProps<
      SearchTrailersResponse,
      unknown,
      SearchTrailersQueryParams,
      SearchTrailersPathParams
    >,
    'path'
  >
) => {
  const { pathParams, ...rest } = props || {}
  const { id = 0 } = pathParams || {}
  return useSearchTrailers({
    id,
    ...rest,
  })
}

export const AutocompleteTrailerNumber = forwardRef<
  HTMLInputElement,
  IAutocompleteTrailerNumberProps
>(({ onChange, shareToken, shipmentId, value: valueProp, ...props }, ref) => {
  const { options = [], loading, refetch } = useAutocompleteRestfulQuery(
    useTrailers,
    {
      queryParams: {
        shareToken,
        term: valueProp?.text || '',
      },
      pathParams: {
        id: shipmentId,
      },
    },
    {
      getOptionsConfig: data => {
        const { trailerNumbers = [] } = data || {}
        return {
          options: trailerNumbers,
          groupIndex: 1,
          mapOption: id => ({
            id,
            text: id,
          }),
        }
      },
    }
  )

  const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
    if (isAutocompleteBaseOption(newVal)) {
      onChange(newVal)
    } else {
      onChange(null)
    }
  }

  const inputChangeHandler: TAutocompleteInputChangeHandler = (_, newVal) => {
    refetch({
      queryParams: {
        shareToken,
        term: newVal,
      },
      pathParams: {
        id: shipmentId,
      },
    })
  }

  const [debouncedInputChangeHandler] = useDebouncedCallback(inputChangeHandler, 250)
  const value = getComputedValue(options, valueProp)

  return (
    <AutocompleteBase
      label='Trailer Number'
      data-test='AutocompleteTrailerNumber'
      ref={ref}
      value={value}
      onChange={changeHandler}
      onInputChange={debouncedInputChangeHandler}
      options={options}
      loading={loading}
      placeholder='Enter your trailer number'
      loadingText='Loading...'
      selectOnFocus
      autoSelect
      clearOnBlur={false}
      fullWidth
      inputIcon='Search'
      renderInputType='withIcon'
      {...props}
    />
  )
})
